/**
 * StrategyConsole — Phase 4.A
 *
 * Per-strategy_id aggregation table. Strategies are the most granular
 * lever you have for finding edge: kill the ones that bleed, promote
 * the ones that print. This view answers the daily question "which
 * strategy_id has been working the last 30d / forever".
 *
 * Pure frontend aggregation over the existing /api/bets feed — no
 * new worker endpoint needed.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { BetsResponse, Bet } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

type SortKey = "n" | "decided" | "win_rate" | "pnl_units" | "avg_clv" | "avg_edge";

interface Aggregated {
  strategy_id:    string;
  sports:         string[];               // sports this strategy covers
  n:              number;
  decided:        number;
  wins:           number;
  losses:         number;
  pending:        number;
  win_rate:       number | null;
  pnl_units:      number;
  avg_clv:        number | null;
  avg_edge:       number | null;
  avg_llm_conf:   number | null;
  last_placed_at: string | null;
}

interface ViewState {
  sortKey:    SortKey;
  sortDir:    "asc" | "desc";
  search:     string;
  minSample:  number;        // hide strategies with fewer decided bets
}
const view: ViewState = {
  sortKey:   "decided",
  sortDir:   "desc",
  search:    "",
  minSample: 0,
};

export function renderStrategyConsole(onChange: () => void): TemplateResult {
  const e = store.get<BetsResponse>("bets");
  if (e.error) return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const all = aggregate(e.data.bets);
  const filtered = applyFilters(all);

  return html`
    <div class="strat-console">
      ${headerCard(all, filtered, onChange)}

      <section class="panel strat-table-panel">
        <div class="strat-table-scroll">
          <table class="strat-table">
            <thead>
              <tr>
                <th>Strategy</th>
                <th>Sports</th>
                ${headerCell("n",         "Bets",     onChange)}
                ${headerCell("decided",   "Decided",  onChange)}
                <th>W / L / P</th>
                ${headerCell("win_rate",  "Win %",    onChange)}
                ${headerCell("pnl_units", "P&L",      onChange)}
                ${headerCell("avg_clv",   "Avg CLV",  onChange)}
                ${headerCell("avg_edge",  "Avg edge", onChange)}
                <th>Last bet</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? html`
                <tr><td colspan="11" class="strat-empty">
                  No strategies match the current filters.
                </td></tr>
              ` : filtered.map(row)}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  `;
}

// =============================================================
// Header card — totals + filters
// =============================================================
function headerCard(
  all: Aggregated[],
  filtered: Aggregated[],
  onChange: () => void,
): TemplateResult {
  const totalDecided = all.reduce((s, a) => s + a.decided, 0);
  const totalPnl     = all.reduce((s, a) => s + a.pnl_units, 0);
  const winning = all.filter((a) => a.pnl_units > 0).length;
  const losing  = all.filter((a) => a.pnl_units < 0).length;

  return html`
    <section class="panel strat-header">
      <div class="strat-header__row">
        <div class="strat-header__metric">
          <span class="strat-header__label">strategies</span>
          <span class="strat-header__value">${all.length}</span>
          <span class="strat-header__sub">${winning} winning · ${losing} losing</span>
        </div>
        <div class="strat-header__metric">
          <span class="strat-header__label">decided bets</span>
          <span class="strat-header__value">${totalDecided}</span>
        </div>
        <div class="strat-header__metric">
          <span class="strat-header__label">total P&amp;L</span>
          <span class="strat-header__value ${totalPnl >= 0 ? "is-pos" : "is-neg"}">
            ${totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}u
          </span>
        </div>

        <span class="strat-header__spacer"></span>

        <input
          class="strat-header__search"
          type="search"
          placeholder="Filter strategy_id…"
          .value=${view.search}
          @input=${(e: Event) => {
            view.search = (e.target as HTMLInputElement).value;
            onChange();
          }}
        />

        <div class="strat-header__chip-group">
          ${[0, 5, 20, 50].map((n) => html`
            <button
              class="strat-header__chip ${view.minSample === n ? "is-active" : ""}"
              @click=${() => { view.minSample = n; onChange(); }}
            >
              n≥${n}
            </button>
          `)}
        </div>

        <span class="strat-header__count">
          ${filtered.length} / ${all.length}
        </span>

        ${renderExplainButton({
          label: "Explain strategy mix",
          context: () => buildOverallExplainContext(all, filtered),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Header cell helper
// =============================================================
function headerCell(key: SortKey, label: string, onChange: () => void): TemplateResult {
  const active = view.sortKey === key;
  const arrow = active ? (view.sortDir === "asc" ? "↑" : "↓") : "";
  return html`
    <th class="strat-table__sortable-th ${active ? "is-active" : ""}"
        @click=${() => {
          if (view.sortKey === key) {
            view.sortDir = view.sortDir === "asc" ? "desc" : "asc";
          } else {
            view.sortKey = key;
            view.sortDir = "desc";
          }
          onChange();
        }}>
      <span class="strat-table__sortable">
        <span>${label}</span>
        <span class="strat-table__sort-arrow">${arrow}</span>
      </span>
    </th>
  `;
}

// =============================================================
// Row
// =============================================================
function row(a: Aggregated): TemplateResult {
  const wr = a.win_rate;
  const wrCls = wr == null ? "is-idle"
              : wr >= 60 ? "is-ok"
              : wr >= 50 ? "is-warn"
              : "is-bad";
  const pnlCls = a.pnl_units > 0 ? "is-pos" : a.pnl_units < 0 ? "is-neg" : "";
  const clvCls = a.avg_clv == null ? "" : a.avg_clv > 0 ? "is-pos" : a.avg_clv < 0 ? "is-neg" : "";

  const lastDate = a.last_placed_at
    ? new Date(a.last_placed_at).toLocaleDateString([], { month: "short", day: "numeric" })
    : "—";

  return html`
    <tr class="strat-row">
      <td class="strat-row__name">${a.strategy_id}</td>
      <td class="strat-row__sports">${a.sports.join(" · ")}</td>
      <td class="strat-row__num">${a.n}</td>
      <td class="strat-row__num">${a.decided}</td>
      <td class="strat-row__num strat-row__wlp">
        <span class="is-pos">${a.wins}</span>/<span class="is-neg">${a.losses}</span>/<span style="color:var(--text-quaternary);">${a.pending}</span>
      </td>
      <td class="strat-row__num">
        <span class="strat-row__win-pill ${wrCls}">
          ${wr == null ? "—" : `${wr.toFixed(1)}%`}
        </span>
      </td>
      <td class="strat-row__num ${pnlCls}">
        ${a.pnl_units >= 0 ? "+" : ""}${a.pnl_units.toFixed(2)}u
      </td>
      <td class="strat-row__num ${clvCls}">
        ${a.avg_clv == null ? "—" : `${a.avg_clv >= 0 ? "+" : ""}${a.avg_clv.toFixed(2)}`}
      </td>
      <td class="strat-row__num">
        ${a.avg_edge == null ? "—" : `${a.avg_edge >= 0 ? "+" : ""}${a.avg_edge.toFixed(1)}pp`}
      </td>
      <td class="strat-row__num strat-row__date">${lastDate}</td>
      <td class="strat-row__action">
        ${renderExplainButton({
          label: "Explain",
          context: () => buildStrategyExplainContext(a),
        })}
      </td>
    </tr>
  `;
}

// =============================================================
// Aggregation
// =============================================================
function aggregate(bets: Bet[]): Aggregated[] {
  const groups: Record<string, Bet[]> = {};
  for (const b of bets) {
    const key = b.strategy_id || "unknown";
    groups[key] = groups[key] ?? [];
    groups[key]!.push(b);
  }

  return Object.entries(groups).map(([strategy_id, gbets]) => {
    const wins    = gbets.filter((b) => b.outcome === "win").length;
    const losses  = gbets.filter((b) => b.outcome === "loss").length;
    const pending = gbets.filter((b) => !b.outcome || b.outcome === "pending").length;
    const decided = wins + losses;
    const sports  = Array.from(new Set(gbets.map((b) => (b.sport || "?").toUpperCase()))).sort();

    const pnl_sum = gbets.reduce((s, b) => s + (b.pnl ?? 0), 0);

    const clvs = gbets.map((b) => b.clv).filter((x): x is number => typeof x === "number");
    const avg_clv = clvs.length > 0 ? clvs.reduce((s, x) => s + x, 0) / clvs.length : null;

    const edges = gbets.map((b) => b.edge_pp).filter((x): x is number => typeof x === "number");
    const avg_edge = edges.length > 0 ? edges.reduce((s, x) => s + x, 0) / edges.length : null;

    const confs = gbets.map((b) => b.llm_confidence).filter((x): x is number => typeof x === "number");
    const avg_llm_conf = confs.length > 0 ? confs.reduce((s, x) => s + x, 0) / confs.length : null;

    const lastTs = gbets
      .map((b) => b.placed_at)
      .filter((x): x is string => !!x)
      .sort()
      .at(-1) ?? null;

    return {
      strategy_id, sports,
      n: gbets.length, decided, wins, losses, pending,
      win_rate: decided > 0 ? (wins / decided) * 100 : null,
      pnl_units: Number(pnl_sum.toFixed(2)),
      avg_clv,
      avg_edge,
      avg_llm_conf,
      last_placed_at: lastTs,
    };
  });
}

function applyFilters(rows: Aggregated[]): Aggregated[] {
  const q = view.search.trim().toLowerCase();
  let out = rows;
  if (view.minSample > 0) {
    out = out.filter((r) => r.decided >= view.minSample);
  }
  if (q) {
    out = out.filter((r) =>
      r.strategy_id.toLowerCase().includes(q) ||
      r.sports.some((s) => s.toLowerCase().includes(q))
    );
  }
  const dir = view.sortDir === "asc" ? 1 : -1;
  out = [...out].sort((a, b) => {
    const av = (a as any)[view.sortKey];
    const bv = (b as any)[view.sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
  return out;
}

// =============================================================
// Explain context builders
// =============================================================
function buildStrategyExplainContext(a: Aggregated): string {
  return [
    `Strategy: ${a.strategy_id}`,
    `Sports: ${a.sports.join(", ") || "—"}`,
    `Bets: ${a.n} total · ${a.decided} decided · ${a.wins}W ${a.losses}L ${a.pending}P`,
    `Win rate: ${a.win_rate != null ? a.win_rate.toFixed(1) + "%" : "—"}`,
    `P&L: ${a.pnl_units >= 0 ? "+" : ""}${a.pnl_units.toFixed(2)}u`,
    `Avg CLV: ${a.avg_clv != null ? a.avg_clv.toFixed(2) : "—"}`,
    `Avg edge: ${a.avg_edge != null ? a.avg_edge.toFixed(2) + "pp" : "—"}`,
    `Avg LLM confidence: ${a.avg_llm_conf != null ? a.avg_llm_conf.toFixed(1) : "—"}`,
    `Last bet: ${a.last_placed_at ?? "—"}`,
  ].join("\n");
}

function buildOverallExplainContext(all: Aggregated[], filtered: Aggregated[]): string {
  const top = [...all].sort((a, b) => b.pnl_units - a.pnl_units).slice(0, 5);
  const bottom = [...all].sort((a, b) => a.pnl_units - b.pnl_units).slice(0, 5);
  return [
    `Strategy console summary:`,
    `${all.length} strategies total · ${filtered.length} match filters`,
    `Decided: ${all.reduce((s, a) => s + a.decided, 0)} bets`,
    `Total P&L: ${all.reduce((s, a) => s + a.pnl_units, 0).toFixed(2)}u`,
    "",
    "Top 5 by P&L:",
    ...top.map((s) => `  ${s.strategy_id} (${s.sports.join("/")}): ${s.decided}n, ${s.pnl_units >= 0 ? "+" : ""}${s.pnl_units.toFixed(2)}u, ${s.win_rate?.toFixed(1) ?? "—"}%`),
    "",
    "Bottom 5 by P&L:",
    ...bottom.map((s) => `  ${s.strategy_id} (${s.sports.join("/")}): ${s.decided}n, ${s.pnl_units >= 0 ? "+" : ""}${s.pnl_units.toFixed(2)}u, ${s.win_rate?.toFixed(1) ?? "—"}%`),
  ].join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="strat-console">
      <section class="panel" style="height:80px;"></section>
      <section class="panel" style="min-height:480px;padding:24px;">
        ${Array.from({ length: 8 }, () => html`
          <div class="skeleton" style="height:18px;margin-bottom:8px;"></div>
        `)}
      </section>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:480px;margin:0 auto;">
        No bets yet — strategies appear once /api/bets has data.
      </div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Strategies</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
